import { FC, ReactNode } from "react";
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material";
import { EntityKind } from "../../../utils/entityPaths";
import {
  MERGER_ANSWER_FIELDS,
  MergerAnswers,
  NEEDED_LABELS
} from "../marketAccessFields";

type Props = {
  kind: EntityKind;
  description: string;
  onDescriptionChange: (value: string) => void;
  answers: MergerAnswers;
  onAnswerChange: (key: keyof MergerAnswers, value: string) => void;
  /** The attachments block: a local file picker on create, the live list on edit. */
  attachments: ReactNode;
};

/**
 * The part of the merger create and edit forms that is not the book's normal form: the
 * description, the Market Access answers (a merger has no Market Access lifecycle, so they are
 * asked here instead) and the merger documents. Controlled; the form owns the state.
 */
export const MergerSection: FC<Props> = ({
  kind,
  description,
  onDescriptionChange,
  answers,
  onAnswerChange,
  attachments
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 3, paddingTop: 1 }}>
    <Divider />
    <Typography variant="h6">Merger</Typography>

    <TextField
      label="Merger description"
      placeholder="Who merged with whom, and what it changes for MFT"
      value={description}
      onChange={(e) => onDescriptionChange(e.target.value)}
      required
      fullWidth
      multiline
      minRows={2}
    />

    <Box>
      <Typography variant="subtitle1" sx={{ marginBottom: 2 }}>
        Market Access answers
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3
        }}
      >
        {MERGER_ANSWER_FIELDS[kind].map((field) =>
          field.input === "text" ? (
            <TextField
              key={field.key}
              label={field.label}
              value={answers[field.key] ?? ""}
              onChange={(e) => onAnswerChange(field.key, e.target.value)}
              required={field.required}
              fullWidth
            />
          ) : (
            <FormControl key={field.key} required={field.required} fullWidth>
              <InputLabel>{field.label}</InputLabel>
              <Select
                label={field.label}
                value={answers[field.key] ?? ""}
                onChange={(e) => onAnswerChange(field.key, e.target.value)}
              >
                {Object.entries(NEEDED_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )
        )}
      </Box>
    </Box>

    <Box>
      <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
        Merger documents
      </Typography>
      {attachments}
    </Box>
  </Box>
);
